import { AnimatePresence, motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Sweet-alert style logout confirmation. We use Radix's Dialog as the
 * accessibility primitive (focus trap + escape handling) but the
 * overlay and content are rendered through framer-motion so we get
 * smooth spring entry/exit instead of Radix's default CSS keyframes.
 *
 * Visuals:
 *  - frosted blur backdrop (`backdrop-blur-md`) so the dashboard
 *    fades behind without going pitch-black
 *  - card scales in from 95% with a soft spring
 *  - destructive-tinted shield icon at the top to telegraph the
 *    "you're about to end your session" stake
 */
export function LogoutConfirm({ open, onOpenChange, onConfirm }: Props) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="fixed inset-0 z-50 bg-black/45 backdrop-blur-md"
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 6 }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 26,
                  mass: 0.7,
                }}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-border bg-card p-6 shadow-2xl"
              >
                {/* Decorative gradient halo behind the icon */}
                <div className="relative mx-auto flex size-16 items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-destructive/15 blur-md" />
                  <span className="relative flex size-14 items-center justify-center rounded-full bg-destructive/15 text-destructive">
                    <ShieldAlert className="size-7" />
                  </span>
                </div>

                <DialogPrimitive.Title asChild>
                  <h2 className="mt-4 text-center text-lg font-bold tracking-tight">
                    Sign out of Afya24 Admin?
                  </h2>
                </DialogPrimitive.Title>
                <DialogPrimitive.Description asChild>
                  <p className="mt-1.5 text-center text-sm text-muted-foreground">
                    Your session will end. You'll need to enter a fresh
                    one-time code to sign back in.
                  </p>
                </DialogPrimitive.Description>

                <div className="mt-6 flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={onConfirm}
                  >
                    <LogOut className="size-4" />
                    Sign out
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
